import React from 'react';
import PropTypes from 'prop-types';
import { min, identity } from 'lodash';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { branch } from '../../../node_module_clones/recompose';
import { GroupTranslatorProvider, useGroupTranslator } from '../../components/i18n/GroupTranslationContext';

// Components
import AttachedCommentsContainer from '../attachedComment/AttachedCommentsContainer';
import AttachedCommentsDropTarget from '../dnd/elementDropTargets/AttachedCommentsDropTarget';
import ImageUploader from './ImageUploader';
import ImageRenderer from './ImageRenderer';
import ColorBar from '../../components/colors/ColorBar';
import Caption from '../../components/caption/Caption';
import ElementUserTag from '../../user/userActivity/ElementUserTag';
import ElementResizeHandle from '../resizing/ElementResizeHandle';
import ElementActivityIndicator from '../activity/ElementActivityIndicator';
import QuickNotesActivityIndicator from '../activity/QuickNotesActivityIndicator';
import QuickLineCreationTool from '../quickLine/QuickLineCreationTool';

// Decorators
import imageDropTargetDecorator from './imageDropTargetDecorator';

// Utils
import { now } from '../../utils/react/propsComparisons';
import getParentScrollableSection from '../../utils/dom/getParentScrollableSection';
import { elementClassNames, getDomElementId } from '../utils/elementUtil';
import { getShouldShowElementCaption } from '../media/mediaElementDisplayUtils';
import { prop, objectSize } from '../../../common/utils/immutableHelper';
import { getNaturalImageWidth } from './imageElementUtils';
import { getData } from '../attachments/attachmentsSelector';
import { getElementStyle, pixelsToGridPoints, scaleToGrid } from '../../utils/grid/gridUtils';
import { getBackgroundColorClasses, getColorCssValue } from '../utils/elementColorStyleUtils';
import {
    getBackgroundColor,
    getColor,
    getImageProp,
    getIsTransparentImage,
    getShowBackground,
    getWidth,
    getTempId,
    getIsDataUri,
    getElementId,
    getCaption,
} from '../../../common/elements/utils/elementPropertyUtils';
import { getShowQuickLineTool } from '../quickLine/quickLineUtil';
import { getOpenFullSizeImageOrNativeCarousel } from './ImageRendererUtils';
import { getIsFeatureEnabledForCurrentUser } from '../feature/elementFeatureSelector';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { IMAGE_SMALL_WIDTH_GRID } from '../../../common/images/imageConstants';
import { ExperimentId } from '../../../common/experiments/experimentsConstants';
import { SKETCH_BACKGROUND_COLORS } from '../../../common/colors/colorConstants';

// Styles
import './Image.scss';

const nowSingleSelected = now('isSingleSelected');

const mapStateToProps = createStructuredSelector({
    isImageModalV2Enabled: getIsFeatureEnabledForCurrentUser(ExperimentId.imageModalV2),
});

// NOTE: This very strangely must be a class component in order to work correctly with React DnD
// If it's not, then "component" is null in the onDrop function of the imageDropTargetDecorator.
@branch(({ allowHoverReplaceImage }) => allowHoverReplaceImage, imageDropTargetDecorator(), identity)
class Image extends React.Component {
    constructor(props) {
        super(props);
        this.imageRef = React.createRef();
    }

    /**
     * Focuses the image when it gets selected, so that it can handle key events,
     * allowing the caption to be immediately edited.
     */
    componentDidUpdate(prevProps) {
        const { isEditing, isElementOpenInModal } = this.props;

        if (!nowSingleSelected(prevProps, this.props)) return;
        if (isEditing) return;
        if (isElementOpenInModal) return;

        const isAlreadyFocused = document.activeElement === this.imageRef.current;
        if (isAlreadyFocused) return;

        const scrollableParent = getParentScrollableSection(this.imageRef.current);
        const initialScrollTop = scrollableParent?.scrollTop;
        const initialScrollLeft = scrollableParent?.scrollLeft;

        this.imageRef.current?.focus();

        // When focusing the image, a default browser scroll might occur, so we want to undo
        //  the scroll if it happens
        if (scrollableParent) {
            scrollableParent.scrollTop = initialScrollTop;
            scrollableParent.scrollLeft = initialScrollLeft;
        }
    }

    render() {
        const {
            currentBoardId,
            elementId,
            element,
            gridSize,
            inList,
            tempSize,
            elementEvents,
            isEditable,
            isLocked,
            attachment,
            isSelected,
            permissions,
            handleKey,
            isPresentational,
            setParentHoveredChildAcceptsDrop,
            getContextZoomScale,
            getContextZoomTranslationPx,
            isImageModalV2Enabled,
        } = this.props;

        const attachmentData = getData(attachment);
        const imageData = getImageProp(element) || attachmentData;
        const hasImage = objectSize(imageData) > 0;

        if (getTempId(element) && !hasImage) return null;

        const colorName = getColor(element);
        const captionContent = getCaption(element);
        const captionVisible = getShouldShowElementCaption(this.props);

        const imageElementSize = getWidth(element);

        // Initially the width is undefined therefore imageWidth would be ELEMENT_DEFAULT_WIDTH for a few ms
        let naturalImageWidth = getNaturalImageWidth({ element, attachmentData });
        if (hasImage && !naturalImageWidth) return null;

        // When adding data:uri images, they tend to be smaller initially and then resolve to larger images later
        naturalImageWidth = getIsDataUri(element)
            ? ELEMENT_DEFAULT_WIDTH * gridSize
            : scaleToGrid(naturalImageWidth, gridSize);

        const imageWidth =
            (tempSize && tempSize.width) ||
            imageElementSize ||
            min([pixelsToGridPoints(naturalImageWidth, gridSize), ELEMENT_DEFAULT_WIDTH]);

        // Only stored transparent images can show the selectable background colour surface.
        const canRenderImageBackground = isImageModalV2Enabled && getIsTransparentImage(element);
        const backgroundColor = canRenderImageBackground ? getBackgroundColor(element) : null;
        const backgroundColorCss = getColorCssValue(backgroundColor, SKETCH_BACKGROUND_COLORS);
        const isTransparent = !backgroundColorCss && !getShowBackground(element) && prop('transparent', imageData);
        // Captions need an opaque surface for either selected image backgrounds or attachment-only transparency.
        const shouldRenderCaptionBackground = canRenderImageBackground || (isImageModalV2Enabled && isTransparent);

        const imageContentClassNames = canRenderImageBackground
            ? classNames('Image-content', {
                  'background-color-target': isSelected,
                  'background-color-contrast-target': isSelected,
                  ...getBackgroundColorClasses(backgroundColor, true, SKETCH_BACKGROUND_COLORS),
              })
            : null;

        const classes = elementClassNames(
            'Image drag-handle focusable',
            {
                ...this.props,
                transparent: isTransparent,
            },
            {
                'small-width': imageWidth < 15,
                'caption-visible': captionVisible,
                'image-background-color': shouldRenderCaptionBackground,
                'minimum-width': imageWidth < IMAGE_SMALL_WIDTH_GRID,
            },
        );

        const openFullSizeImageOrNativeCarousel = getOpenFullSizeImageOrNativeCarousel(this.props);

        const displayElement = hasImage ? (
            <ImageRenderer
                {...this.props}
                isTransparent={isTransparent}
                openFullSizeImageOrNativeCarousel={openFullSizeImageOrNativeCarousel}
            />
        ) : (
            <ImageUploader {...this.props} />
        );

        return (
            <div
                id={getDomElementId(getElementId(element))}
                className={classes}
                style={getElementStyle(imageWidth, gridSize, inList)}
                onKeyDown={handleKey}
                {...elementEvents}
                tabIndex="-1"
                ref={this.imageRef}
            >
                <ColorBar colorName={colorName} isSelected={isSelected} />
                <QuickLineCreationTool
                    show={getShowQuickLineTool(this.props)}
                    elementId={elementId}
                    element={element}
                    currentBoardId={currentBoardId}
                    gridSize={gridSize}
                    getContextZoomScale={getContextZoomScale}
                    getContextZoomTranslationPx={getContextZoomTranslationPx}
                />
                <ElementActivityIndicator {...this.props} />
                <QuickNotesActivityIndicator {...this.props} />
                <ElementUserTag {...this.props} />

                {canRenderImageBackground ? (
                    <div className={imageContentClassNames} style={{ backgroundColor: backgroundColorCss }}>
                        {displayElement}
                    </div>
                ) : (
                    displayElement
                )}

                <Caption
                    {...this.props}
                    padTop
                    placeholder={this.props.captionPlaceholder}
                    textContent={captionContent}
                    captionVisible={captionVisible}
                />

                <AttachedCommentsDropTarget
                    setParentHoveredChildAcceptsDrop={setParentHoveredChildAcceptsDrop}
                    isRelative
                    gridSize={gridSize}
                    isPresentational={isPresentational}
                    element={element}
                    elementId={elementId}
                    getContextZoomScale={getContextZoomScale}
                />
                <AttachedCommentsContainer
                    elementId={elementId}
                    element={element}
                    gridSize={gridSize}
                    isPresentational={isPresentational}
                    currentBoardId={currentBoardId}
                    permissions={permissions}
                />
                <ElementResizeHandle {...this.props} showHandle={!isLocked && isEditable && !inList} />
            </div>
        );
    }
}

Image.propTypes = {
    currentBoardId: PropTypes.string,
    elementId: PropTypes.string,
    element: PropTypes.object.isRequired,
    attachment: PropTypes.object,
    gridSize: PropTypes.number,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isElementOpenInModal: PropTypes.bool,
    isEditing: PropTypes.bool,
    canDrop: PropTypes.bool,
    inList: PropTypes.string,
    uploadFile: PropTypes.func,
    isHovered: PropTypes.bool,
    tempSize: PropTypes.object,
    className: PropTypes.string,
    elementEvents: PropTypes.object,
    isEditable: PropTypes.bool,
    isLocked: PropTypes.bool,
    filterQuery: PropTypes.string,
    filterState: PropTypes.string,
    permissions: PropTypes.number,
    isPresentational: PropTypes.bool,
    handleKey: PropTypes.func,
    setParentHoveredChildAcceptsDrop: PropTypes.func,
    getContextZoomScale: PropTypes.func,
    getContextZoomTranslationPx: PropTypes.func,
    captionPlaceholder: PropTypes.string,
    isImageModalV2Enabled: PropTypes.bool,
};

const ImageWithTranslation = (props) => {
    const groupTranslate = useGroupTranslator();
    return <Image {...props} captionPlaceholder={groupTranslate('caption.placeholder')} />;
};

const ImageWrapped = (props) => {
    return (
        <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.image">
            <ImageWithTranslation {...props} />
        </GroupTranslatorProvider>
    );
};

export default connect(mapStateToProps)(ImageWrapped);
